package org.moxultimate.topwithmox.wallpaper.details

import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.launch
import org.moxultimate.topwithmox.core.base.ViewModel
import org.moxultimate.topwithmox.core.state.APIState
import org.moxultimate.topwithmox.wallpaper.api.WallpaperRepository
import org.moxultimate.topwithmox.wallpaper.data.WallpaperResponse
import org.moxultimate.topwithmox.wallpaper.data.WallpaperSeriesRequest
import org.moxultimate.topwithmox.wallpaper.ui.WallpaperState

class WallpaperDetailsViewModel(
    private val repository: WallpaperRepository,
) : ViewModel() {

    /** State for the single wallpaper shown at the top of the details page. */
    private val _wallpaperDetail = MutableStateFlow<APIState<WallpaperResponse>>(APIState.Loading)
    val wallpaperDetail: StateFlow<APIState<WallpaperResponse>> = _wallpaperDetail

    private val _screenState = MutableStateFlow<WallpaperState>(WallpaperState.Loading)
    val screenState: StateFlow<WallpaperState> = _screenState

    private val _isLoadingMore = MutableStateFlow(false)

    /** True while a "load more" page request is in-flight. */
    val isLoadingMore: StateFlow<Boolean> = _isLoadingMore

    /** Accumulated list across all loaded pages. */
    private val accumulatedWallpapers = mutableListOf<WallpaperResponse>()

    /** ID of the last wallpaper in the current page; used as cursor for the next page. */
    private var lastId: Long = 0L

    /** Set to false once the API returns fewer than 20 items, signalling no more pages. */
    private var hasMore = true

    /**
     * Fetches the wallpaper by [id], then automatically loads related wallpapers by series.
     * Called on first open — handles both direct URL access and in-app navigation.
     */
    fun loadWallpaperById(id: Long) {
        viewModelScope.launch {
            repository.getWallpaperById(id).collect { state ->
                _wallpaperDetail.value = state
                if (state is APIState.Success) {
                    loadAllWallpapersBySeries(state.data.series)
                }
            }
        }
    }

    /** Initial load — resets all pagination state and fetches the first page. */
    fun loadAllWallpapersBySeries(series: String) {
        accumulatedWallpapers.clear()
        lastId = 0L
        hasMore = true
        getAllWallpapersBySeries(isInitialLoad = true, series = series)
    }

    /**
     * Appends the next page to the existing list.
     * No-op if already loading or there are no more pages.
     */
    fun loadMoreWallpapersBySeries(series: String) {
        if (_isLoadingMore.value || !hasMore) return
        getAllWallpapersBySeries(isInitialLoad = false, series = series)
    }

    private fun getAllWallpapersBySeries(isInitialLoad: Boolean, series: String) {
        viewModelScope.launch {
            _isLoadingMore.value = true
            repository.getWallpapersBySeries(
                WallpaperSeriesRequest(series = series, lastId = lastId)
            ).collect { response ->
                _screenState.value = when (response) {
                    is APIState.Loading -> {
                        if (isInitialLoad) WallpaperState.Loading else _screenState.value
                    }

                    is APIState.Error -> {
                        _isLoadingMore.value = false
                        WallpaperState.Error(response.error, response.statusCode)
                    }

                    is APIState.Success -> {
                        val page = response.data
                        _isLoadingMore.value = false

                        if (page.size < 20) hasMore = false
                        accumulatedWallpapers.addAll(page)
                        if (page.isNotEmpty()) lastId = accumulatedWallpapers.last().id

                        val combined = accumulatedWallpapers.toList()
                        WallpaperState.Success(
                            wallpaperList = combined,
                            typeList = combined.map { it.type }.distinct(),
                            hasMore = hasMore,
                        )
                    }
                }
            }
        }
    }


}